import React, { ReactNode, useMemo, useState } from 'react';

import { PassIndex, TrackedSessionDashboardMode } from '../../../../../../types/common.types';
import { PassDetail, PassEntry, Programme, TrackedSessionDashboard } from '../../../../../../types/entity.types';

import { findPassOverviewByIndex } from '../../../../../../utils/accountability.util';
import { getClassNames } from '../../../../../../utils/util';

import { ReactComponent as ArrowIcon } from '../../../../../../resources/icons/arrow-single.svg';
import { ReactComponent as CloseIcon } from '../../../../../../resources/icons/close.svg';
import { ReactComponent as FirstPlaceIcon } from '../../../../../../resources/icons/first-place.svg';
import { ReactComponent as MoonIcon } from '../../../../../../resources/icons/moon.svg';
import { ReactComponent as PauseIcon } from '../../../../../../resources/icons/pause.svg';
import { ReactComponent as PlayIcon } from '../../../../../../resources/icons/play.svg';
import { ReactComponent as SecondPlaceIcon } from '../../../../../../resources/icons/second-place.svg';
import { ReactComponent as SunIcon } from '../../../../../../resources/icons/sun.svg';
import { ReactComponent as ThirdPlaceIcon } from '../../../../../../resources/icons/third-place.svg';

import './accountability-tracked-session-dashboard.scss';

type Props = {
  show: boolean;
  programme?: Programme;
  activePassIndex: PassIndex;
  passIndices: PassIndex[];
  mode: TrackedSessionDashboardMode;
  isPaused: boolean;
  dashboard?: TrackedSessionDashboard;
  passDetail?: PassDetail;
  onPassOverviewClick: (passIndex: PassIndex) => void;
  onModeClick: (mode: TrackedSessionDashboardMode) => void;
  onTogglePause: () => void;
  onClose: () => void;
};

const AccountabilityTrackedSessionDashboard = ({
  show,
  programme,
  activePassIndex,
  passIndices,
  mode,
  isPaused,
  dashboard,
  passDetail,
  onPassOverviewClick,
  onModeClick,
  onTogglePause,
  onClose
}: Props) => {
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');

  const sectionName = useMemo(() => {
    if (!dashboard) return '';

    if (activePassIndex.index === -1) {
      return `${mode} Overall Standings`;
    } else {
      const passOverview = findPassOverviewByIndex(dashboard, activePassIndex);
      return `${mode} ${passOverview?.label} Standings`;
    }
  }, [dashboard, activePassIndex, mode]);

  const columns = useMemo(() => {
    if (!passDetail) return [];

    const sortedLeaderboard = [...passDetail.leaderboard].sort(
      (entryOne, entryTwo) => entryOne.position - entryTwo.position
    );

    if (sortedLeaderboard.length <= 10) {
      return [sortedLeaderboard.reverse()];
    }

    const left = sortedLeaderboard.slice(0, 10);
    const right = sortedLeaderboard.slice(10).slice(-10);
    return [left.reverse(), right.reverse()];
  }, [passDetail]);

  if (!show) return null;

  const isLightTheme = theme === 'light';
  const isTeamMode = mode === 'Teams';

  const renderRow = (entry: PassEntry, isSplit: boolean) => {
    let position: ReactNode;
    switch (entry.position) {
      case 1:
        position = <FirstPlaceIcon />;
        break;

      case 2:
        position = <SecondPlaceIcon />;
        break;

      case 3:
        position = <ThirdPlaceIcon />;
        break;

      default:
        position = (
          <p
            className={getClassNames('accountability-tracked-session-dashboard__row__position', {
              light: isLightTheme
            })}
          >
            {entry.position}
          </p>
        );
    }

    const isPodium = entry.position < 4;

    return (
      <div
        className={getClassNames('accountability-tracked-session-dashboard__row', {
          podium: isPodium,
          split: isSplit,
          'podium-light': isPodium && isLightTheme
        })}
        key={entry.position}
      >
        {position}
        <div
          className={getClassNames('accountability-tracked-session-dashboard__row__name', {
            light: isLightTheme,
            team: isTeamMode,
            'team-light-podium': isTeamMode && isLightTheme && isPodium
          })}
        >
          <p>{entry.name}</p>
          {entry.isManualEntry && <p className="accountability-tracked-session-dashboard__row__manual-entry">Manual</p>}
        </div>

        <p
          className={getClassNames('accountability-tracked-session-dashboard__row__team', {
            'podium-light': isPodium && isLightTheme
          })}
        >
          {entry.teamNames.join(', ')}
        </p>

        <p className={getClassNames('accountability-tracked-session-dashboard__row__time', { light: isLightTheme })}>
          {entry.time}
        </p>
      </div>
    );
  };

  const currentSlotIndex = passIndices.findIndex(
    (index) => index.index === activePassIndex.index && index.subIndex === activePassIndex.subIndex
  );

  const renderPass = (passIndex: PassIndex) => (
    <button
      key={`${passIndex.index}-${passIndex.subIndex}`}
      className={getClassNames('accountability-tracked-session-dashboard__controls__button', {
        active: passIndex.index === activePassIndex.index && passIndex.subIndex === activePassIndex.subIndex,
        light: isLightTheme
      })}
      onClick={() => onPassOverviewClick(passIndex)}
    >
      {findPassOverviewByIndex(dashboard!, passIndex)?.label}
    </button>
  );

  const renderEllipsis = (key: string, boundaryPassIndex: PassIndex) => (
    <button
      key={key}
      className={getClassNames('accountability-tracked-session-dashboard__controls__button', {
        light: isLightTheme
      })}
      onClick={() => onPassOverviewClick(boundaryPassIndex)}
    >
      ...
    </button>
  );

  const renderPasses = () => {
    const total = passIndices.length;

    if (total <= 5) return passIndices.map(renderPass);

    let windowStart = currentSlotIndex - 1;
    let windowEnd = currentSlotIndex + 1;

    if (windowStart <= 1) windowStart = 0;
    if (windowEnd >= total - 2) windowEnd = total - 1;

    const realButtonCount = windowEnd - windowStart + 1;
    const hasLeadingEllipsis = windowStart > 0;
    const hasTrailingEllipsis = windowEnd < total - 1;
    const visibleSlotCount = realButtonCount + (hasLeadingEllipsis ? 1 : 0) + (hasTrailingEllipsis ? 1 : 0);
    const shortfall = 5 - visibleSlotCount;

    if (shortfall > 0) {
      if (hasLeadingEllipsis) {
        windowStart -= shortfall;
      } else {
        windowEnd += shortfall;
      }
    }

    const elements: ReactNode[] = [];

    if (windowStart > 0) elements.push(renderEllipsis('ellipsis-start', passIndices[0]!));
    for (let index = windowStart; index <= windowEnd; index++) {
      elements.push(renderPass(passIndices[index]!));
    }
    if (windowEnd < total - 1) elements.push(renderEllipsis('ellipsis-end', passIndices[total - 1]!));

    return elements;
  };

  const previousPassIndex = passIndices[currentSlotIndex - 1];

  const handleDecrementClick = () => {
    if (!previousPassIndex) return;
    onPassOverviewClick(previousPassIndex);
  };

  const nextPassIndex = passIndices[currentSlotIndex + 1];

  const handleIncrementClick = () => {
    if (!nextPassIndex) return;
    onPassOverviewClick(nextPassIndex);
  };

  const handleThemeToggleClick = () => {
    setTheme((prevTheme) => (prevTheme === 'dark' ? 'light' : 'dark'));
  };

  return (
    <div className={getClassNames('accountability-tracked-session-dashboard', { light: isLightTheme })}>
      <div className={getClassNames('accountability-tracked-session-dashboard__header', { light: isLightTheme })}>
        <button className="accountability-tracked-session-dashboard__close-button" onClick={onClose}>
          <CloseIcon />
        </button>
        <p>Dashboard</p>
        <p>{sectionName}</p>
        <p>{programme?.name}</p>
      </div>

      <div className={getClassNames('accountability-tracked-session-dashboard__rows', { split: columns.length > 1 })}>
        {columns.map((column, columnIndex) => (
          <div className="accountability-tracked-session-dashboard__column" key={columnIndex}>
            {column.map((entry) => renderRow(entry, columns.length > 1))}
          </div>
        ))}
      </div>

      <div className="accountability-tracked-session-dashboard__controls">
        <div className="accountability-tracked-session-dashboard__controls__left">
          <button
            className={getClassNames('accountability-tracked-session-dashboard__controls__button', {
              light: isLightTheme
            })}
            onClick={handleDecrementClick}
            disabled={!previousPassIndex}
          >
            <ArrowIcon />
          </button>
          {renderPasses()}
          <button
            className={getClassNames('accountability-tracked-session-dashboard__controls__button', {
              light: isLightTheme
            })}
            onClick={handleIncrementClick}
            disabled={!nextPassIndex}
          >
            <ArrowIcon />
          </button>
        </div>
        <div className="accountability-tracked-session-dashboard__controls__right">
          <button
            className={getClassNames('accountability-tracked-session-dashboard__controls__button', {
              active: !isTeamMode,
              light: isLightTheme
            })}
            onClick={() => onModeClick('Individual')}
          >
            Individual
          </button>
          <button
            className={getClassNames('accountability-tracked-session-dashboard__controls__button', {
              active: isTeamMode,
              light: isLightTheme
            })}
            onClick={() => onModeClick('Teams')}
          >
            Teams
          </button>
          <button
            onClick={handleThemeToggleClick}
            className={getClassNames('accountability-tracked-session-dashboard__controls__icon', {
              light: isLightTheme
            })}
          >
            {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
          </button>
          <button
            onClick={onTogglePause}
            className={getClassNames('accountability-tracked-session-dashboard__controls__icon', {
              light: isLightTheme
            })}
          >
            {isPaused ? <PlayIcon /> : <PauseIcon />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountabilityTrackedSessionDashboard;
